import React, { useCallback, useEffect, useState } from 'react';
import { Col, Dropdown, Row, Table, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { toast } from 'react-toastify';
import { getOperationsCockpitAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './OperationsCockpit.module.scss';

function queueVariant(status) {
  if (status === 'warn') return 'warning';
  if (status === 'critical') return 'danger';
  return 'success';
}

export default function OperationsCockpit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOperationsCockpitAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Operations Cockpit"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Dashboard', path: platformUrl('dashboard') },
            { label: 'Cockpit' },
          ]}
        />
        <LoadingSkeleton rows={8} cols={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Operations Cockpit"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Dashboard', path: platformUrl('dashboard') },
            { label: 'Cockpit' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load cockpit"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  const chipClass = (s) => {
    if (s === 'healthy') return styles.chipOk;
    if (s === 'degraded') return styles.chipWarn;
    return styles.chipBad;
  };

  return (
    <div>
      <PageHeader
        title="Operations Cockpit"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Dashboard', path: platformUrl('dashboard') },
          { label: 'Cockpit' },
        ]}
        secondaryActions={[
          {
            label: 'Retry All Retryable',
            onClick: () => setConfirmRetry(true),
          },
          {
            label: 'Open Runbooks',
            onClick: () => navigate(platformUrl('support')),
          },
        ]}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm" id="pause-connector-dd">
            Pause Connector
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {['Microsoft', 'Google', 'WhatsApp', 'OpenAI'].map((n) => (
              <Dropdown.Item key={n} onClick={() => toast.info(`${n} pause is simulated`)}>
                {n}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className={`${styles.incidentBar} mb-4 p-2 rounded-3 d-flex flex-wrap gap-2 align-items-center`}>
        <span className="small text-muted me-2">Global status</span>
        {(data.providers || []).map((p) => (
          <span key={p.id} className={`${styles.providerChip} ${chipClass(p.status)}`}>
            {p.name}: {p.status}
          </span>
        ))}
        <span className="small text-muted ms-auto">Deploy {data.deployVersion}</span>
      </div>

      <Row className="g-3">
        <Col lg={8}>
          <Row className="g-3">
            {(data.queues || []).map((q) => (
              <Col md={6} key={q.name}>
                <button
                  type="button"
                  className={`${styles.queueWidget} w-100 text-start border-0 p-3 rounded-3`}
                  onClick={() => navigate(`${platformUrl('jobs')}?queue=${encodeURIComponent(q.name)}`)}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <strong>{q.name}</strong>
                    <span className="badge bg-secondary">{q.depth} depth</span>
                  </div>
                  <div className="small text-muted mb-1">
                    Throughput {q.throughputPerMin}/min · Failures {q.failureCount}
                  </div>
                  <ProgressBar
                    now={q.utilizationPct}
                    variant={queueVariant(q.status)}
                    label={`${q.utilizationPct}%`}
                    className={styles.queueBar}
                  />
                </button>
              </Col>
            ))}
          </Row>
        </Col>
        <Col lg={4}>
          <div className={`${styles.sidePanel} p-3 mb-3`}>
            <h6>Incident timeline</h6>
            <ul className="list-unstyled small mb-0">
              {(data.incidentTimeline || []).map((ev) => (
                <li key={ev.id} className="mb-2 pb-2 border-bottom">
                  <div className="text-muted">{new Date(ev.ts).toLocaleString()}</div>
                  {ev.message}
                </li>
              ))}
            </ul>
          </div>
          <div className={`${styles.sidePanel} p-3 mb-3`}>
            <h6>Regions</h6>
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Status</th>
                  <th>Tenants</th>
                </tr>
              </thead>
              <tbody>
                {(data.regions || []).map((r) => (
                  <tr key={r.code}>
                    <td>{r.code}</td>
                    <td>{r.healthy ? 'OK' : 'Degraded'}</td>
                    <td>{r.tenantsAffected}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className={`${styles.sidePanel} p-3`}>
            <h6>Webhooks ({data.webhookHourly?.windowLabel})</h6>
            <div className="d-flex gap-3">
              <span>4xx: {data.webhookHourly?.count4xx}</span>
              <span>5xx: {data.webhookHourly?.count5xx}</span>
            </div>
          </div>
        </Col>
      </Row>

      <div className={`${styles.bottomTable} mt-4 p-3 bg-white rounded-3`}>
        <h6 className="mb-3">Recent support escalations</h6>
        <Table responsive hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Tenant</th>
              <th>Issue</th>
              <th>Owner</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.supportEscalations || []).map((r) => (
              <tr key={r.ticket}>
                <td>{r.ticket}</td>
                <td>{r.tenant}</td>
                <td>{r.issue}</td>
                <td>{r.owner}</td>
                <td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <ConfirmModal
        isOpen={confirmRetry}
        onClose={() => setConfirmRetry(false)}
        onConfirm={() => {
          setConfirmRetry(false);
          toast.success('Retry queued (simulated)');
        }}
        title="Retry all retryable jobs?"
        message="This will enqueue retries for all eligible failed jobs in DLQ."
        confirmLabel="Retry"
      />
    </div>
  );
}
